/**
 * location-inventory.js
 *
 * Fetches per-location inventory counts from the internal Cloud Run API and
 * injects them into the inventory-status block on product pages.
 *
 * API: https://inventory-3jtovkbtxq-uc.a.run.app/{variantId}
 * Response: { inventory: [{location: "01"|"02"|"99", available: N}], availableOnline: N, oversell: bool }
 *
 * Location codes:
 *   "01" → Charlesbourg
 *   "02" → Saint-Nicolas
 *   "99" → Online / warehouse (count comes from availableOnline field)
 *
 * No API token required. Falls back to Liquid content on error.
 */

(function () {
  'use strict';

  const API_BASE = 'https://inventory-3jtovkbtxq-uc.a.run.app';

  // Maps location code → internal key used throughout this module.
  function getLocationKey(code) {
    if (code === '01') return 'charlesbourg';
    if (code === '02') return 'saint-nicolas';
    return 'online';
  }

  // Returns the current value of a CSS custom property from :root.
  function cssVar(name) {
    return getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  }

  // ─── API ───────────────────────────────────────────────────────────────────

  async function fetchInventoryData(variantId) {
    const res = await fetch(`${API_BASE}/${variantId}`);
    if (!res.ok) throw new Error(`Inventory API error: ${res.status}`);
    return res.json();
  }

  // ─── Rendering ─────────────────────────────────────────────────────────────

  // Builds and injects per-location inventory messages. Returns true if anything was rendered.
  function renderLocationInventory(block, data, threshold, showNotice) {
    const colorLow     = cssVar('--color-products-low-inventory');
    const colorStock   = cssVar('--color-products-in-stock');
    const colorSoldOut = cssVar('--color-products-sold-out');

    // Translated message templates come from Liquid data attributes.
    // low_stock uses location-first format: "Saint-Nicolas : 2 disponibles"
    const tplLowStock        = block.dataset.msgLowStock    || '__LOCATION__: __COUNT__ available';
    const tplLowStockOne     = block.dataset.msgLowStockOne || tplLowStock;
    const tplInStock         = block.dataset.msgInStock         || 'In stock';
    const tplSoldOut         = block.dataset.msgSoldOut         || 'Out of stock';
    const tplPickupAvailable = block.dataset.msgPickupAvailable || 'Available for pickup';

    const locationOrder = [
      { key: 'online',        label: block.dataset.labelOnline       || 'Online'        },
      { key: 'charlesbourg',  label: block.dataset.labelCharlesbourg || 'Charlesbourg'  },
      { key: 'saint-nicolas', label: block.dataset.labelSaintNicolas || 'Saint-Nicolas' },
    ];

    const oversell        = data?.oversell        || false;
    const availableOnline = data?.availableOnline ?? 0;

    // Build qty map. Online stock always comes from availableOnline (more accurate).
    const qtys = { online: availableOnline };
    for (const item of (data?.inventory || [])) {
      const key = getLocationKey(item.location);
      if (key !== 'online') qtys[key] = item.available;
    }

    const messages     = [];
    let dominantColor  = colorSoldOut;

    for (const { key, label } of locationOrder) {
      if (!(key in qtys)) continue;

      const qty     = qtys[key];
      const isStore = key !== 'online';
      let text  = null;
      let color = colorSoldOut;

      if (oversell) {
        // When oversell is on, all locations are treated as available.
        if (showNotice === 'always') {
          text  = `${label} : ${tplInStock}`;
          color = colorStock;
        }
      } else if (qty > 0) {
        if (threshold > 0 && qty <= threshold) {
          // Low stock: show exact count with location-first format.
          const tpl = qty === 1 ? tplLowStockOne : tplLowStock;
          text  = tpl.replace('__COUNT__', qty).replace('__LOCATION__', label);
          color = colorLow;
        } else if (showNotice === 'always') {
          // Above threshold: show generic "in stock" with location-first format.
          text  = `${label} : ${tplInStock}`;
          color = colorStock;
        }
      } else if (isStore && availableOnline > 0) {
        // Physical store is out, but online stock exists → pickup possible.
        // Always show regardless of showNotice — it's actionable info.
        text  = `${label} : ${tplPickupAvailable}`;
        color = colorStock;
      } else if (showNotice === 'always') {
        // Completely out of stock at this location.
        text  = `${label} : ${tplSoldOut}`;
        color = colorSoldOut;
      }

      if (text) {
        messages.push({ text, color });
        if (color === colorLow)                            dominantColor = colorLow;
        else if (color === colorStock && dominantColor !== colorLow) dominantColor = colorStock;
      }
    }

    if (messages.length === 0) return false;

    // Replace Liquid-rendered fallback with accurate per-location lines.
    block.querySelectorAll('.inventory-status__message').forEach((el) => el.remove());
    block.style.setProperty('--message-color', dominantColor);

    for (const { text, color } of messages) {
      const div = document.createElement('div');
      div.className   = 'inventory-status__message';
      div.style.color = color;
      div.textContent = text;
      block.appendChild(div);
    }

    return true;
  }

  // ─── Block processing ───────────────────────────────────────────────────────

  async function processBlock(block) {
    if (block.dataset.inventoryLoaded) return;

    const variantId  = block.dataset.variantId;
    const threshold  = parseInt(block.dataset.threshold, 10) || 0;
    const showNotice = block.dataset.showNotice || 'when-inventory-low';

    if (!variantId) return;

    // Mark immediately to prevent duplicate calls triggered by the MutationObserver.
    block.dataset.inventoryLoaded = 'pending';

    try {
      const data     = await fetchInventoryData(variantId);
      const rendered = renderLocationInventory(block, data, threshold, showNotice);
      block.dataset.inventoryLoaded = rendered ? 'true' : 'false';
    } catch (err) {
      // Silent fail: the Liquid fallback content remains visible.
      console.warn('[location-inventory]', err.message);
      delete block.dataset.inventoryLoaded;
    }
  }

  // ─── Initialisation ─────────────────────────────────────────────────────────

  function init() {
    // Process blocks present on initial page load.
    document
      .querySelectorAll('[js-product-block-inventory-status][data-variant-id]')
      .forEach((block) => processBlock(block));

    // Watch for variant changes: the theme swaps the block's outerHTML when the
    // customer picks a different size/colour, so the new element has no
    // data-inventory-loaded and we process it automatically.
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const added of mutation.addedNodes) {
          if (added.nodeType !== Node.ELEMENT_NODE) continue;

          const blocks = added.matches('[js-product-block-inventory-status][data-variant-id]')
            ? [added]
            : Array.from(added.querySelectorAll('[js-product-block-inventory-status][data-variant-id]'));

          for (const block of blocks) processBlock(block);
        }
      }
    });

    // Scope the observer to product sections only, for performance.
    document.querySelectorAll('[js-product-section]').forEach((section) => {
      observer.observe(section, { childList: true, subtree: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
